import { Callout, Checkbox, FormGroup, FormLabel, Icon, Textarea } from '~/components/primitives';
import { std } from '@funkey/shared/utils';
import { graphql } from '@funkey/types/graphql/gql';
import type {
    CateringItem,
    CateringMeta,
    CateringMetaItem,
    CateringType,
} from '@funkey/types/graphql/graphql';
import type { I18n } from '@funkey/locales';
import { cx } from 'class-variance-authority';
import { useEffect, useMemo } from 'react';
import Title from '~/components/utils/Title';
import { api } from '~/modules';
import type { FormState } from '~/modules/form';

export namespace CheckoutCateringFields {
    export type FormValues = {
        catering: Omit<CateringMeta, '__typename'>;
    };

    const QUERY = graphql(`
        query CateringItems {
            cateringItems {
                id
                name
                description
                type
                priceMin
                priceMax
                recommended
                imagePath
            }
        }
    `);

    export const CATERING_TYPE_META = (i18n: I18n) =>
        ({
            Breakfast: {
                label: i18n._('Breakfast'),
            },
            Lunch: {
                label: i18n._('Lunch'),
            },
            Dinner: {
                label: i18n._('Dinner'),
            },
            DrinksAndExtras: {
                label: i18n._('Drinks and Extras'),
            },
        }) as const satisfies Record<CateringType, Record<string, unknown>>;

    export const Component: React.FC<{
        i18n: I18n;
        formState: FormState.Subform<CheckoutCateringFields.FormValues>;
        participants?: number | null;
    }> = props => {
        const isCateringEnabled =
            typeof props.participants === 'number' && props.participants >= 15;

        const cateringQuery = api.useGraphqlQuery(QUERY);

        const groupedCateringItems = useMemo<
            {
                type: CateringType;
                label: string;
                items: CateringItem[];
            }[]
        >(() => std.Object.entries(CheckoutCateringFields.CATERING_TYPE_META(props.i18n)).map(([type, meta]) => ({
            ...meta,
            type,
            items: cateringQuery.data?.cateringItems.filter(item => item.type === type) ?? [],
        })), [cateringQuery.data]); // prettier-ignore

        // Reset catering to defaults when catering becomes unavailable
        useEffect(() => {
            if (!isCateringEnabled) {
                props.formState.handleChange('catering', defaultValues().catering);
            }
        }, [isCateringEnabled]);

        if (!isCateringEnabled) {
            return (
                <Callout
                    type="warning"
                    title={props.i18n._('checkout.extra.catering.unavailable')}
                    description={props.i18n._('checkout.extra.catering.unavailable.description')}
                />
            );
        }

        const handleCateringSelection = (cateringType: CateringType, currentSelected: boolean) => {
            const newCatering = { ...props.formState.values.catering };
            newCatering[cateringType] = {
                ...newCatering[cateringType],
                selected: !currentSelected,
                ids: !currentSelected ? [] : newCatering[cateringType].ids,
            };
            props.formState.handleChange('catering', newCatering);
        };

        const handleContainerClick = (
            cateringType: CateringType,
            currentSelected: boolean,
            event: React.MouseEvent,
        ) => {
            // Don't trigger if clicking on checkbox, label, or textarea
            const target = event.target as HTMLElement;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'LABEL' ||
                target.tagName === 'TEXTAREA' ||
                target.closest('textarea')
            ) {
                return;
            }

            handleCateringSelection(cateringType, currentSelected);
        };

        return (
            <fieldset>
                <Title as="h3" className="mb-6" size="xl" weight="bold">
                    {props.i18n._('Catering')}
                </Title>
                <div className="rounded-2xl sm:rounded-4xl">
                    {groupedCateringItems?.length > 0 &&
                        groupedCateringItems.map((cateringGroup, index) => {
                            const formValue = props.formState.values.catering[cateringGroup.type];

                            return (
                                <div
                                    key={cateringGroup.type}
                                    className={cx(
                                        'relative cursor-pointer transition-all duration-200',
                                        'z-0 -mb-[2px] flex flex-col gap-4 border-2 px-6 py-8 last:mb-0 hover:z-2',
                                        // Base styling
                                        formValue.selected
                                            ? 'border-secondary bg-secondary-50'
                                            : 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
                                        // Border radius for first and last items
                                        {
                                            'rounded-t-2xl sm:rounded-t-4xl': index === 0,
                                            'rounded-b-2xl sm:rounded-b-4xl':
                                                index === groupedCateringItems.length - 1,
                                        },
                                        formValue.selected && 'z-1',
                                    )}
                                    onClick={event =>
                                        handleContainerClick(
                                            cateringGroup.type,
                                            formValue.selected,
                                            event,
                                        )
                                    }
                                    // Mouse hit area only: keyboard users toggle the checkbox inside
                                    role="presentation"
                                >
                                    <div className="items-top flex gap-3">
                                        <div className="select-none">
                                            <Checkbox
                                                id={`catering-${cateringGroup.type}`}
                                                checked={formValue.selected}
                                                value={cateringGroup.type}
                                                onChange={() => {
                                                    handleCateringSelection(
                                                        cateringGroup.type,
                                                        formValue.selected,
                                                    );
                                                }}
                                            />
                                        </div>
                                        <div className="mt-[-3px] flex flex-col gap-2 select-none">
                                            <FormLabel
                                                size="md"
                                                label={cateringGroup.label}
                                                htmlFor={`catering-${cateringGroup.type}`}
                                            />
                                            <span className="text-sm text-blue-500">
                                                {(() => {
                                                    switch (cateringGroup.type) {
                                                        case 'Breakfast':
                                                            return props.i18n._(
                                                                'checkout.extra.catering.breakfast.description',
                                                            );
                                                        case 'Lunch':
                                                            return props.i18n._(
                                                                'checkout.extra.catering.lunch.description',
                                                            );
                                                        case 'Dinner':
                                                            return props.i18n._(
                                                                'checkout.extra.catering.dinner.description',
                                                            );
                                                        case 'DrinksAndExtras':
                                                            return props.i18n._(
                                                                'checkout.extra.catering.drinksAndExtras.description',
                                                            );
                                                        default:
                                                            return '';
                                                    }
                                                })()}
                                            </span>
                                        </div>
                                        <Icon
                                            name={`catering_${std.String.toLowerCase(
                                                cateringGroup.type,
                                            )}`}
                                            viewBox="0 0 48 48"
                                            size="xl"
                                            strokeWidth={2}
                                            className={cx(
                                                'ml-auto',
                                                formValue.selected
                                                    ? 'text-secondary'
                                                    : 'text-blue-500',
                                            )}
                                        />
                                    </div>
                                    {formValue.selected ? (
                                        <div className={cx('flex flex-col sm:ml-6')}>
                                            {/* <CateringItemDisplay
                                                position="first"
                                                checked={formValue.suggest}
                                                onChange={() => {
                                                    props.formState.handleChange<
                                                        any,
                                                        typeof formValue.suggest
                                                    >(
                                                        `catering.${cateringGroup.type}.suggest`,
                                                        !formValue.suggest,
                                                    );
                                                }}
                                                item={{
                                                    id: 0,
                                                    name: props.i18n._(
                                                        'checkout.extra.catering.suggestion',
                                                    ),
                                                    priceMin: 0,
                                                    priceMax: 0,
                                                }}
                                                vat={vat}
                                                shouldDisplayPrices={shouldDisplayPrices}
                                                i18n={props.i18n}
                                            /> */}
                                            {/* Simulate table border collapse: Disables border-top of the element after an active element */}
                                            {/* <div className="flex flex-col rounded-b-2xl border-b [&>.active+button]:mt-0 [&>.active+button]:border-t-0">
                                                {cateringGroup.items.map((item, index) => (
                                                    <CateringItemDisplay
                                                        position={
                                                            cateringGroup.items.length - 1 === index
                                                                ? 'last'
                                                                : 'middle'
                                                        }
                                                        key={item.id}
                                                        checked={formValue.ids.includes(item.id)}
                                                        onChange={() => {
                                                            props.formState.handleChange<
                                                                any,
                                                                typeof formValue.ids
                                                            >(
                                                                `catering.${cateringGroup.type}.ids`,
                                                                std.Array.toggle(
                                                                    formValue.ids,
                                                                    item.id,
                                                                ),
                                                            );
                                                        }}
                                                        item={item}
                                                        i18n={props.i18n}
                                                        vat={vat}
                                                        shouldDisplayPrices={shouldDisplayPrices}
                                                    />
                                                ))}
                                            </div> */}

                                            <FormGroup
                                                label={props.i18n._('Remarks')}
                                                htmlFor={`remarks-${cateringGroup.type}}`}
                                                variant="no_spacing"
                                            >
                                                <div>
                                                    <Textarea
                                                        id={`remarks-${cateringGroup.type}}`}
                                                        value={formValue.remarks}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLTextAreaElement>,
                                                        ) => {
                                                            props.formState.handleChange<
                                                                any,
                                                                typeof formValue.remarks
                                                            >(
                                                                `catering.${cateringGroup.type}.remarks`,
                                                                e.currentTarget.value,
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </FormGroup>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                </div>
            </fieldset>
        );
    };

    export function defaultValues(): CheckoutCateringFields.FormValues {
        const DEFAULT_CATERING_META_ITEM = {
            selected: false,
            ids: [],
            remarks: '',
            suggest: false,
        } satisfies CateringMetaItem;

        return {
            catering: {
                Breakfast: DEFAULT_CATERING_META_ITEM,
                Lunch: DEFAULT_CATERING_META_ITEM,
                Dinner: DEFAULT_CATERING_META_ITEM,
                DrinksAndExtras: DEFAULT_CATERING_META_ITEM,
            },
        };
    }
}

// const CateringItemDisplay: React.FC<{
//     i18n: I18n;
//     item: Pick<
//         CateringItem,
//         'id' | 'name' | 'priceMin' | 'priceMax' | 'description' | 'recommended' | 'imagePath'
//     >;
//
//     checked: boolean;
//     onChange: (value: number) => void;
//     position: 'first' | 'middle' | 'last';
//     vat: ReturnType<typeof useVATRate>;
//     shouldDisplayPrices: ReturnType<typeof useShouldDisplayPrices>;
// }> = props => (
//     <button
//         key={props.item.id}
//         type="button"
//         onClick={() => props.onChange(props.item.id)}
//         className={cx(
//             'relative flex items-center gap-5 border border-blue-200 p-3',
//             props.checked
//                 ? 'active border-2 border-b-2 border-secondary'
//                 : 'border-b-0 border-blue-200',
//             {
//                 'rounded-t-2xl': !props.item.id || props.position === 'first',
//                 'rounded-b-2xl': props.position === 'last',
//             },
//         )}
//     >
//         <div className="relative overflow-hidden p-3 text-left max-sm:py-4 sm:h-[90px] sm:min-w-[135px] sm:rounded-lg sm:bg-secondary-50">
//             <Checkbox
//                 checked={props.checked}
//                 value={props.item.id}
//                 onChange={() => props.onChange(props.item.id)}
//                 className="left-2 top-2 z-1 flex sm:absolute"
//             />
//             {props.item.imagePath ? (
//                 <Image
//                     src={props.item.imagePath}
//                     alt={props.item.name}
//                     height={90}
//                     width={135}
//                     className="absolute left-0 top-0 z-0 hidden sm:block"
//                 />
//             ) : (
//                 <Icon
//                     name="catering_suggestion"
//                     className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 text-secondary sm:block"
//                     size="xl"
//                     strokeWidth={2}
//                     viewBox="0 0 48 48"
//                 />
//             )}
//         </div>
//
//         <div className="flex flex-1 items-center gap-y-2 max-sm:flex-col max-sm:items-start">
//             <div className="flex w-full flex-col items-start">
//                 <FormLabel label={props.item.name} size="md" className="text-left" />
//                 {props.item.description ? (
//                     <p className="text-left text-sm text-blue-500">{props.item.description}</p>
//                 ) : null}
//             </div>
//
//             {props.shouldDisplayPrices && props.item.priceMin > 0 && (
//                 <span className="whitespace-nowrap text-blue-400">
//                     <I18n_AmountPerParticipant
//                         abbreviated
//                         disableEURPrefix
//                         amount={i18n =>
//                             Units.formatPriceRangeEUR(i18n, {
//                                 minPrice: props.vat.calcEUR(props.item.priceMin),
//                                 maxPrice: props.vat.calcEUR(props.item.priceMax),
//                             })
//                         }
//                     />
//                 </span>
//             )}
//
//             {props.item.recommended && (
//                 <div
//                     className={cx(
//                         'rounded-bl-2xl bg-secondary px-2 py-1 text-xs font-medium text-white max-sm:-order-1 max-sm:rounded-md sm:absolute sm:-right-px sm:-top-px sm:px-5 sm:py-2 sm:text-sm',
//                         {
//                             'rounded-tr-2xl': props.first,
//                         },
//                     )}
//                 >
//                     {props.i18n._('Recommended')}
//                 </div>
//             )}
//         </div>
//     </button>
// );
